// @ts-ignore esModuleInterop
import React from 'react'
import { graph } from '@adc/constants'
// @ts-ignore no types
import { ScrollSpy } from '@adc/ui-adc'
// @ts-ignore no types
import { get } from 'lodash'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import * as blueprint from '@adc/blueprint'
import { formatMoneyWithNumeral, formatPhoneNumber } from './formatters'
import { getMarketingAttribute } from './graph'

const unescape = (html: string) => {
  if (!html) return html
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

const notDash = (str: string) => Boolean(str) && str !== '-'

type PrepareForAuctionListItem =
  | {
      title?: string
      body?: string | JSX.Element
      unchecked?: boolean
    }
  | string

export type PrepareForAuctionContent = {
  note?: string
  list: PrepareForAuctionListItem[]
}

// If type is plain it will return an array of strings
const getPrepareForAuctionContent = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing'],
  isPlain: boolean
): PrepareForAuctionContent => {
  let note
  let list: PrepareForAuctionListItem[] = []
  const partnershipDetails = get(listing, 'trustee.partnership_details', '')
  if (!blueprint.listing.utils.isTrustee(listing)) return { note, list }
  if (blueprint.listing.utils.isFclOnlineAuction(listing)) {
    const closingCompanyData =
      blueprint.listing.utils.getClosingCompanyData(listing)
    const saleDeposit = get(listing, 'trustee.online_state_deposit_amount', 0)
    note =
      'This is a cash-only auction. Conventional financing cannot be used for this property.'
    list = [
      {
        title: 'Payment Information:',
        body: isPlain ? (
          // TODO: remove as that when formatters.js file is typed
          `Wire the sale deposit of ${
            formatMoneyWithNumeral(saleDeposit, '$0') as number
          } within 2 hours after the sale to the title company:
            • ${closingCompanyData.party_name} ${closingCompanyData.address}
            ${
              closingCompanyData.party_phone
                ? // TODO: remove as when formatter.js is typed
                  `• ${
                    formatPhoneNumber(closingCompanyData.party_phone) as string
                  }`
                : ''
            }
            ${
              closingCompanyData.party_email
                ? `• ${closingCompanyData.party_email}`
                : ''
            }`
        ) : (
          <>
            Wire the <strong>sale deposit</strong> of{' '}
            {formatMoneyWithNumeral(saleDeposit, '$0')} within 2 hours after the
            sale to the title company.
            <ul>
              {(notDash(closingCompanyData.party_name) ||
                notDash((closingCompanyData.address || '').trim())) && (
                <li>
                  {notDash(closingCompanyData.party_name) &&
                    closingCompanyData.party_name}{' '}
                  {closingCompanyData.address}
                </li>
              )}
              {notDash(closingCompanyData.party_phone) && (
                <li>
                  <a href={`tel:${closingCompanyData.party_phone}`}>
                    {formatPhoneNumber(closingCompanyData.party_phone)}
                  </a>
                </li>
              )}
              {notDash(closingCompanyData.party_email) && (
                <li>
                  <a href={`mailto:${closingCompanyData.party_email}`}>
                    {closingCompanyData.party_email}
                  </a>
                </li>
              )}
            </ul>
          </>
        ),
      },
      {
        title: 'Pay the remaining balance on time.',
        body: isPlain ? (
          'Balances on the purchase price must be paid within 30 days after the sale is confirmed by the court. If you don’t pay the remaining balance on time, the sale will be void and you may lose your sale deposit.'
        ) : (
          <>
            Balances on the purchase price must be paid within{' '}
            <strong>30 days after the sale is confirmed by the court.</strong>{' '}
            If you don’t pay the remaining balance on time, the sale will be
            void and you may lose your sale deposit.
          </>
        ),
      },
      {
        title: 'Pay all closing costs',
        body: 'and those costs‚ allowances and taxes that aren’t covered by the proceeds of the sale. This is the buyer’s responsibility.',
      },
      {
        title: 'Pre-pay current taxes on the property',
        body: 'prior to the confirmation of the sale. This is the buyer’s responsibility.',
      },
      {
        title: 'Bid on the property again',
        body: 'The property may have a new auction start date (or provisional date) if the property does not sell the first time.',
      },
      {
        unchecked: true,
        title: 'Provisional Sale Date:',
        // TODO: remove when biddingStatus is removed
        // @ts-ignore biddingStatus is not typed
        body: listing.auction?.provisional_sale_date as string,
        /*
        was: (biddingStatus as Record<string, unknwon>)
          ?.provisionalSaleTime as string
        */
      },
      {
        title: 'Read the Sale Terms and Conditions',
        body: isPlain ? (
          'and top FAQs before you bid on court-ordered foreclosure properties.'
        ) : (
          <>
            and{' '}
            <ScrollSpy selector='#faqs'>
              {({ scrollTo }: { scrollTo: () => void }) => (
                <a onClick={scrollTo}>Top FAQs</a>
              )}
            </ScrollSpy>{' '}
            before you bid on court-ordered foreclosure properties.
          </>
        ),
      },
      {
        unchecked: true,
        title: 'Legal Info:',
      },
      {
        body: 'Ohio statute 2329.152(E)(3) requires that the title and closing company be chosen by and engaged by the Private Selling Officer for this transaction.',
      },
    ].filter(Boolean)

    if (partnershipDetails) {
      list.push({
        title: 'Private Selling Officer:',
        body: isPlain ? unescape(partnershipDetails) : partnershipDetails,
      })
    }

    return {
      note,
      list: isPlain
        ? list
            .slice(0, 2)
            .map(listItem => {
              if (typeof listItem === 'string') {
                return ''
              }
              const { title, body } = listItem
              return `${title || ''} ${
                typeof body === 'string' ? body : '' || ''
              }`
            })
            .filter(
              listItem =>
                !(typeof listItem === 'string' && listItem.length === 0)
            )
        : list,
    }
  } else if (
    blueprint.listing.utils.isFCLMarketing(listing) ||
    listing?.listing_configuration?.is_third_party_online
  ) {
    const summary = get(listing, 'primary_property.summary', {})
    let isFlorida = false
    let isColorado = false

    if ('address' in summary) {
      const address = summary.address
      const propertyState = get(address, 'country_primary_subdivision', '')
      isFlorida = propertyState === 'Florida'
      isColorado = propertyState === 'Colorado'
    }

    note =
      'Conventional financing cannot be used for this property. All foreclosed properties are sold as is, where is.'
    list.push(
      {
        title: 'Register on county site:',
        body: `You'll need to register for an auction directly on the county site${
          isColorado ? ' at least 1 day before the auction' : ''
        }. Provide the information that's requested and create a username and password.`,
      },
      {
        title: 'Deposit:',
        body: isFlorida
          ? `Florida counties require a deposit equal to 5% of your highest bid. The deposit will be due 1-5 days before the auction, depending on the county. Check the county website for details.`
          : 'Check the foreclosure notice or contact the county or Public Trustee directly for specific information.',
      },
      {
        title: 'Payment Information:',
        body: isFlorida
          ? `Counties typically require payment within 24 hours. Check your county website for types of payments accepted and when payment is required. Most counties accept wired funds and cashier's checks.`
          : `Typically counties require funds to be paid in the form of cash, bank money order or certified check at the time of sale. Personal checks, company checks, letters of credit, or charge cards may not be accepted. Be sure to check the foreclosure notice or contact the county or Public Trustee directly for specific information.`,
      },
      {
        title: 'Documentation:',
        body: `If you're buying under a separate entity, you'll need specific documentation. ${
          isFlorida
            ? 'Check the county website for specific information.'
            : 'Check the foreclosure notice or contact the county or Public Trustee directly for specific information.'
        }`,
      }
    )
  } else {
    const { message: msgForChecks } = getMarketingAttribute(
      listing,
      // TODO: upgrade sdk to get latest types. Currently if you switch from @adc/constants
      // to @adc/sdk it will break the unit test
      // @ts-ignore upgrade sdk
      graph.enums.MarketingAttributeType.DEPOSIT_CHECK_VERBIAGE // eslint-disable-line
    ) as { message: string }

    note = 'Conventional financing cannot be used for this property.'
    if (msgForChecks) {
      list.push({
        title: 'Payment information:',
        body: isPlain ? (
          unescape(msgForChecks)
        ) : (
          <>
            <span
              dangerouslySetInnerHTML={{
                __html: msgForChecks,
              }}
            />
          </>
        ),
      })
    }
    if (partnershipDetails) {
      list.push({
        title: 'Sales agent contact info:',
        body: isPlain ? unescape(partnershipDetails) : partnershipDetails,
      })
    }
    list.push(
      {
        title: 'Government-issued ID',
        body: "(e.g., driver's license or passport)",
      },
      {
        title: 'Save time at the auction:',
        body: isPlain ? (
          'Download the app (https://auction.onelink.me/WqvL/7185fc99) to get a QR code.'
        ) : (
          <span>
            <a
              href='https://auction.onelink.me/WqvL/7185fc99'
              target='_blank'
              rel='noreferrer'
            >
              Download the app
            </a>{' '}
            to get a QR code.
          </span>
        ),
      }
    )
  }

  return {
    note,
    list: isPlain
      ? list
          .map(listItem => {
            if (typeof listItem === 'string') {
              return ''
            }

            const { title, body } = listItem

            return `${title || ''} ${
              typeof body === 'string' ? body : '' || ''
            }`
          })
          .filter(
            listItem => !(typeof listItem === 'string' && listItem.length === 0)
          )
      : list,
  }
}

export default getPrepareForAuctionContent
